import { useI18n } from 'core/hooks/useI18n';
import { ComponentProps } from 'react';
import ReactSelect, { mergeStyles } from 'react-select';
import type { GroupBase } from 'react-select';
import { useSelectStyles } from './Select.styles';

export type DefaultOption = { label: string; value: string };

// Only add more custom props if you can't accomplish what you need to through react-select props
export type CustomProps = {
  isError?: boolean;
};
export type SelectProps<
  Option = DefaultOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = ComponentProps<typeof ReactSelect<Option, IsMulti, Group>> & CustomProps;

export const MAX_MENU_HEIGHT = 240;

/**
 * `<select />` component, allowing for filtering, multiple item selection, or use as a regular, native `<select />` element.
 * Accepts the same props as `react-select` - https://react-select.com/props.
 * Pass a generic Option to the component that represents the data type for the array of options.
 */
export const Select = <
  Option = DefaultOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: SelectProps<Option, IsMulti, Group>
) => {
  const { styles, value, id, ...rest } = props;
  const { l10n } = useI18n('app', 'action');
  const mergedStyles = useMergedStyles<Option, IsMulti, Group>(props);

  return (
    <ReactSelect<Option, IsMulti, Group>
      styles={mergedStyles}
      maxMenuHeight={MAX_MENU_HEIGHT}
      placeholder={l10n('select')}
      value={value ?? null} // Value isn't set if it's undefined
      inputId={id}
      {...rest}
    />
  );
};

function useMergedStyles<
  Option = DefaultOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: SelectProps<Option, IsMulti, Group>) {
  const { styles, isError } = props;
  const defaultStyles = useSelectStyles<Option, IsMulti, Group>({ isError });
  return mergeStyles(defaultStyles, styles);
}
